import React from "react";
import { useStepperContext } from "./StepperContext";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, CheckCircle, Save, SkipForward } from "lucide-react";
import { StepperControlsProps } from "./types";
import { useLanguage } from "@/contexts/LanguageContext";

export const StepperControls: React.FC<StepperControlsProps> = ({
  backLabel = "Back",
  nextLabel = "Next",
  completeLabel = "Complete",
  skipLabel = "Skip",
  hideBackOnFirst = false,
  hideNextOnLast = false,
  showSkip = true,
  onSkip,
}) => {
  const { t } = useLanguage();
  const {
    activeStep,
    goToPreviousStep,
    goToNextStep,
    isFirstStep,
    isLastStep,
    canGoToNextStep,
    canGoToPreviousStep,
    isStepOptional,
    isStepCompleted,
  } = useStepperContext();

  const isCurrentStepOptional = isStepOptional(activeStep);
  const isCurrentStepCompleted = isStepCompleted(activeStep);
  const canNext = canGoToNextStep();

  const handleSkip = () => {
    if (isCurrentStepOptional) {
      if (onSkip) {
        onSkip();
      }
      goToNextStep();
    }
  };

  return (
    <div className="flex flex-col mt-6 gap-3">
      {/* Skip notification (when applicable) */}
      {isCurrentStepOptional && !isCurrentStepCompleted && showSkip && (
        <div className="flex items-center justify-center w-full mb-1">
          <div className="text-gray-500 text-sm italic flex items-center border border-gray-200 rounded-lg bg-gray-50 p-2 px-3">
            <span className="mr-2">{t("common.skipExplanation")}</span>
            <Button variant="link" size="sm" onClick={handleSkip} className="text-teal-600 font-medium p-0 h-auto">
              {skipLabel}
              <SkipForward className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Navigation buttons */}
      <div className="flex justify-between w-full">
        {/* Back Button */}
        {(!isFirstStep() || !hideBackOnFirst) && (
          <Button
            variant="ghost"
            className="text-teal-700 border-teal-600"
            onClick={goToPreviousStep}
            disabled={!canGoToPreviousStep()}
          >
            <ArrowLeft className="w-4 h-4 mr-2 text-teal-700" />
            {backLabel}
          </Button>
        )}

        {/* Right side buttons */}
        <div className="flex gap-2 ml-auto">
          {/* Next/Complete Button */}
          {(!isLastStep() || !hideNextOnLast) && (
            <Button
              onClick={goToNextStep}
              disabled={!canNext}
              variant="teal"
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              {isLastStep() ? (
                <>
                  {completeLabel}
                  <Save className="w-4 h-4 ml-2 text-white" />
                </>
              ) : (
                <>
                  {nextLabel}
                  <ArrowRight className="w-4 h-4 ml-2 text-white" />
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
